export const subjects = [
  "comprehensive",
  "regulations",
  "practice",
  "case_analysis",
] as const;

export type Subject = (typeof subjects)[number];

export const priorityLevels = ["S", "A", "B"] as const;
export type PriorityLevel = (typeof priorityLevels)[number];

export const learningMethods = ["memorize", "understand", "calculate", "case"] as const;
export type LearningMethod = (typeof learningMethods)[number];

export const knowledgeMapScopes = ["chapter", "session"] as const;
export type KnowledgeMapScope = (typeof knowledgeMapScopes)[number];

export const knowledgeMapNodeKinds = [
  "concept",
  "rule",
  "formula",
  "threshold",
  "pitfall",
  "question_type",
  "output",
] as const;

export type KnowledgeMapNodeKind = (typeof knowledgeMapNodeKinds)[number];

export const masteryLevels = ["unknown", "learning", "uncertain", "stable"] as const;
export type MasteryLevel = (typeof masteryLevels)[number];

export interface KnowledgeMapNode {
  id: string;
  kind: KnowledgeMapNodeKind;
  label: string;
  detail?: string;
  priority?: PriorityLevel;
  mastery?: MasteryLevel;
  sourceRefs?: string[];
}

export interface KnowledgeMapEdge {
  id: string;
  from: string;
  to: string;
  label?: string;
}

export interface KnowledgeMap {
  id: string;
  scope: KnowledgeMapScope;
  title: string;
  version: number;
  nodes: KnowledgeMapNode[];
  edges: KnowledgeMapEdge[];
  sourceRefs: string[];
  updatedReason?: string;
}

export interface StudySource {
  id: string;
  title: string;
  documentId?: string;
  bookPages?: string;
  pdfPages?: string;
  knowledgeModuleId?: string;
  note?: string;
}

export interface AcceptanceCriterion {
  id: string;
  description: string;
  metric?: "count" | "rate" | "minutes" | "binary" | "coverage";
  target?: number;
  unit?: string;
}

export interface StudyStep {
  id: string;
  order: number;
  title: string;
  description: string;
  plannedMinutes: number;
}

export interface StudyLessonReference {
  title: string;
  locator?: string;
  url?: string;
}

export interface StudyLessonExample {
  id: string;
  prompt: string;
  answer: string;
  reasoning: string[];
  trap?: string;
}

export interface StudyLessonMemoryAidItem {
  token: string;
  meaning: string;
}

export interface StudyLessonSection {
  id: string;
  title: string;
  priority: PriorityLevel;
  /** 业务计划 v3 起必填；引用本学习段 knowledgeMap 中由本节精讲覆盖的节点。 */
  knowledgeMapNodeIds?: string[];
  /** 业务计划 v3 起必填；用一句话点明零基础学习者最容易卡住的判断。 */
  hardestPoint?: string;
  explanation: string[];
  memoryAid?: string;
  memoryAidBreakdown?: StudyLessonMemoryAidItem[];
  examTriggers?: string[];
  pitfalls: string[];
  examples: StudyLessonExample[];
}

export interface StudyLessonComparison {
  title: string;
  columns: string[];
  rows: string[][];
}

export interface StudyLesson {
  overview: string;
  learningStrategy: string[];
  textbookLocator: string;
  references?: StudyLessonReference[];
  sections: StudyLessonSection[];
  comparisons?: StudyLessonComparison[];
  recallPrompts?: string[];
}

export interface PracticeSelectionItem {
  cluster: string;
  count: number;
}

export interface PracticeSessionActivity {
  type: "practice";
  externalSource: string;
  selectionPlan: PracticeSelectionItem[];
  targetQuestions: number;
  targetRate: number;
}

export interface CaseActivityQuestion {
  id: string;
  prompt: string;
  points: number;
  responseType?: "single" | "multiple" | "boolean" | "written";
  correctAnswer?: string;
  /** 标记本题是否可进入首次闭卷基线；旧内容未标时保持兼容。 */
  attemptKind?: AttemptKind;
}

export interface CaseActivityRubricItem {
  questionId: string;
  scoringPoints: string[];
}

export interface CaseSessionActivity {
  type: "case";
  stem: string;
  facts: string[];
  questions: CaseActivityQuestion[];
  rubric: CaseActivityRubricItem[];
  referenceOutline: string[];
}

export interface SimulationActivitySection {
  subject: Subject;
  questionCount: number;
  targetCorrect: number;
}

export interface SimulationSessionActivity {
  type: "simulation";
  timeLimitMinutes: number;
  sections: SimulationActivitySection[];
  totalTargetCorrect: number;
}

export type SessionActivity =
  | PracticeSessionActivity
  | CaseSessionActivity
  | SimulationSessionActivity;

export const sessionKinds = [
  "new_knowledge",
  "practice",
  "recall",
  "case_training",
  "simulation",
] as const;
export type SessionKind = (typeof sessionKinds)[number];

export const sessionStatuses = [
  "not_started",
  "in_progress",
  "paused",
  "completed",
  "partial",
  "skipped",
  "revisit",
] as const;
export type SessionStatus = (typeof sessionStatuses)[number];

export interface StudySession {
  id: string;
  order: number;
  title: string;
  kind: SessionKind;
  plannedMinutes: number;
  priority: PriorityLevel;
  methods: LearningMethod[];
  objectives: string[];
  knowledgeMap: KnowledgeMap;
  lesson?: StudyLesson;
  activity?: SessionActivity;
  sources: StudySource[];
  steps: StudyStep[];
  acceptanceCriteria: AcceptanceCriterion[];
  requiredEvidence: string[];
  status: SessionStatus;
  actualMinutes?: number;
  startedAt?: string;
  endedAt?: string;
  incompleteReason?: string;
}

export const macroBlockKinds = [
  "knowledge_and_practice",
  "practice_and_review",
  "case_training",
  "simulation",
] as const;
export type MacroBlockKind = (typeof macroBlockKinds)[number];

export const macroBlockStatuses = [
  "not_started",
  "in_progress",
  "completed",
  "partial",
  "skipped",
] as const;
export type MacroBlockStatus = (typeof macroBlockStatuses)[number];

export interface MacroBlock {
  id: string;
  order: number;
  subject: Subject;
  title: string;
  kind: MacroBlockKind;
  recommendedWindow: string;
  plannedMinutes: number;
  chapterMapId: string;
  sessions: StudySession[];
  status: MacroBlockStatus;
  actualMinutes?: number;
  incompleteReason?: string;
}

export const dayTypes = ["full", "half_rest", "simulation", "light_close"] as const;
export type DayType = (typeof dayTypes)[number];

export const studyDayStatuses = [
  "draft",
  "issued",
  "acknowledged",
  "in_progress",
  "awaiting_report",
  "submitted",
  "oral_review",
  "revision_required",
  "accepted",
  "archived",
  "rescheduled",
] as const;
export type StudyDayStatus = (typeof studyDayStatuses)[number];

export const planningAuditStages = [
  "initial_generation",
  "gap_check_1",
  "gap_check_2",
] as const;
export type PlanningAuditStage = (typeof planningAuditStages)[number];

export interface PlanningAuditEntry {
  stage: PlanningAuditStage;
  checkedAt: string;
  evidence: string[];
  result: string;
  changed: boolean;
  changeSummary: string;
}

export interface StudyDay {
  id: string;
  date: string;
  timezone: string;
  phase: string;
  dayType: DayType;
  title: string;
  summary: string;
  status: StudyDayStatus;
  planVersion: number;
  /** 教学内容结构版本；与业务计划修订号 planVersion 相互独立。 */
  contentFormatVersion?: number;
  /** 交接后“初稿＋两轮查漏”实际审计轨迹；旧计划可缺省。 */
  planningAuditTrail?: PlanningAuditEntry[];
  plannedMinutes: number;
  availableMinutes: number;
  chapterMaps: KnowledgeMap[];
  macroBlocks: MacroBlock[];
  issuedAt?: string;
  acknowledgedAt?: string;
  startedAt?: string;
  submittedAt?: string;
  acceptedAt?: string;
}

export const attemptKinds = ["first", "redo"] as const;
export type AttemptKind = (typeof attemptKinds)[number];

export const practiceModes = ["chapter", "mixed", "simulation"] as const;
export type PracticeMode = (typeof practiceModes)[number];

export const practiceEvidenceDetailStatuses = ["quick", "detailed"] as const;
export type PracticeEvidenceDetailStatus = (typeof practiceEvidenceDetailStatuses)[number];

export const practiceTimingEvidenceStatuses = ["unknown", "measured"] as const;
export type PracticeTimingEvidenceStatus = (typeof practiceTimingEvidenceStatuses)[number];

export interface ConfidenceCounts {
  sure: number;
  uncertain: number;
  unknown: number;
}

export interface PracticeAttempt {
  id: string;
  dayId: string;
  sessionId: string;
  subject: Subject;
  chapter: string;
  topic?: string;
  source: string;
  mode: PracticeMode;
  attemptKind: AttemptKind;
  rawQuestionCount: number;
  duplicateQuestionCount: number;
  correctCount: number;
  wrongCount: number;
  unansweredCount: number;
  understoodQuestionCount: number;
  elapsedMinutes: number | null;
  lookedUpDuringAttempt: boolean;
  confidence: ConfidenceCounts;
  evidenceDetailStatus?: PracticeEvidenceDetailStatus;
  timingEvidenceStatus?: PracticeTimingEvidenceStatus;
  startedAt?: string;
  endedAt?: string;
  notes?: string;
}

export const mistakeCauseCodes = ["K", "M", "C", "R", "P", "F", "O"] as const;
export type MistakeCauseCode = (typeof mistakeCauseCodes)[number];

export const reviewIntervals = ["D1", "D3", "D7"] as const;
export type ReviewInterval = (typeof reviewIntervals)[number];

export const reviewResultStatuses = ["pending", "correct", "incorrect", "skipped"] as const;
export type ReviewResultStatus = (typeof reviewResultStatuses)[number];

export interface MistakeReviewItem {
  interval: ReviewInterval;
  dueDate: string;
  status: ReviewResultStatus;
  completedAt?: string;
  note?: string;
}

export const mistakeStatuses = ["active", "temporarily_mastered", "reactivated"] as const;
export type MistakeStatus = (typeof mistakeStatuses)[number];

export const mistakeDetailStatuses = ["draft", "reviewed"] as const;
export type MistakeDetailStatus = (typeof mistakeDetailStatuses)[number];

export interface Mistake {
  id: string;
  createdDate: string;
  dayId: string;
  practiceAttemptId?: string;
  subject: Subject;
  knowledgeCluster: string;
  questionType: string;
  questionSummary: string;
  learnerAnswer: string;
  correctConclusion: string;
  evidence: string;
  causeCode: MistakeCauseCode;
  preventionRule: string;
  attachmentIds: string[];
  knowledgeMapNodeIds: string[];
  reviews: MistakeReviewItem[];
  status: MistakeStatus;
  /** 白天仅录题目摘要时为 draft；晚间补齐结论、依据与防错规则后改为 reviewed。 */
  detailStatus?: MistakeDetailStatus;
}

export const caseTypes = [
  "nuclear_technology",
  "waste_and_decommissioning",
  "transport",
  "quality_assurance",
] as const;
export type CaseType = (typeof caseTypes)[number];

export const caseDimensionKeys = [
  "type_and_problem",
  "classification_and_evidence",
  "cause_chain",
  "consequences",
  "measures",
  "experience_feedback",
  "legal_basis",
  "structure_and_language",
] as const;
export type CaseDimensionKey = (typeof caseDimensionKeys)[number];

export interface CaseDimensionAssessment {
  dimension: CaseDimensionKey;
  score?: number;
  maxScore?: number;
  note?: string;
}

export interface CaseQuestionResponse {
  questionId: string;
  response: string;
  score: number;
  maxScore: number;
  autoGraded: boolean;
  /** 随逐题答案持久化并进入晚报协作包，防止重做题误计首次。 */
  attemptKind?: AttemptKind;
}

export const caseSubmissionStatuses = [
  "draft",
  "submitted",
  "revision_required",
  "resubmitted",
  "accepted",
] as const;
export type CaseSubmissionStatus = (typeof caseSubmissionStatuses)[number];

export interface CaseSubmission {
  id: string;
  dayId: string;
  sessionId: string;
  title: string;
  caseType: CaseType;
  source: string;
  closedBook: boolean;
  elapsedMinutes: number;
  answerAttachmentIds: string[];
  referenceAttachmentIds: string[];
  answerText?: string;
  questionResponses?: CaseQuestionResponse[];
  selfCoverageRate?: number;
  dimensions: CaseDimensionAssessment[];
  missingPoints: string[];
  rewriteScope?: string;
  rewriteDueDate?: string;
  parentSubmissionId?: string;
  version: number;
  status: CaseSubmissionStatus;
  submittedAt?: string;
}

export const reportStatuses = ["draft", "submitted", "revision_required", "accepted"] as const;
export type ReportStatus = (typeof reportStatuses)[number];

export const reportSubmissionTimings = ["normal", "makeup_before_noon", "overdue"] as const;
export type ReportSubmissionTiming = (typeof reportSubmissionTimings)[number];

export interface BlockOutcome {
  macroBlockId: string;
  status: "completed" | "partial" | "skipped";
  actualMinutes: number;
  incompleteReason?: string;
}

export interface DailyReport {
  id: string;
  dayId: string;
  date: string;
  version: number;
  status: ReportStatus;
  submissionTiming?: ReportSubmissionTiming;
  delayReason?: string;
  sleepHours?: number;
  effectiveStudyMinutes?: number;
  energyLevel?: number;
  concentrationLevel?: number;
  healthConstraints?: string;
  hardConstraints?: string;
  blockOutcomes: BlockOutcome[];
  practiceAttemptIds: string[];
  mistakeIds: string[];
  caseSubmissionIds: string[];
  unknownPoints: string[];
  mostEffectiveMethod?: string;
  mostWastefulMethod?: string;
  tomorrowAvailableMinutes?: number;
  attachmentIds: string[];
  submittedAt?: string;
  acceptedAt?: string;
}

export const reviewEventKinds = [
  "report_submitted",
  "oral_question_asked",
  "oral_answer_submitted",
  "mastery_assessed",
  "revision_requested",
  "revision_submitted",
  "day_accepted",
  "next_plan_issued",
] as const;
export type ReviewEventKind = (typeof reviewEventKinds)[number];

export const reviewActors = ["learner", "coach", "system"] as const;
export type ReviewActor = (typeof reviewActors)[number];

export interface ReviewEvent {
  id: string;
  dayId: string;
  kind: ReviewEventKind;
  actor: ReviewActor;
  createdAt: string;
  questionId?: string;
  prompt?: string;
  answer?: string;
  knowledgeMapNodeIds?: string[];
  /** 仅 mastery_assessed 使用，格式为 subject:具体知识簇。 */
  weaknessClusterId?: string;
  masteryGrade?: "A" | "B" | "C" | "D";
  evidence?: string;
  requestedItems?: string[];
  relatedPlanRevisionId?: string;
}

export const planRevisionStatuses = ["draft", "issued", "acknowledged", "superseded"] as const;
export type PlanRevisionStatus = (typeof planRevisionStatuses)[number];

export const planChangeKinds = [
  "replace_focus",
  "reduce_new_knowledge",
  "add_revisit",
  "change_question_target",
] as const;
export type PlanChangeKind = (typeof planChangeKinds)[number];

export interface PlanChange {
  id: string;
  macroBlockId: string;
  kind: PlanChangeKind;
  before: string;
  after: string;
  reason: string;
  acceptanceCriteria: string[];
}

export interface PlanRevision {
  id: string;
  planDate: string;
  fromVersion: number;
  toVersion: number;
  status: PlanRevisionStatus;
  createdAt: string;
  sourceDayId: string;
  evidence: string[];
  changes: PlanChange[];
  preservedRequirements: string[];
  overloadProtectionApplied: boolean;
  acknowledgedAt?: string;
}

export interface StudySeed {
  version: number;
  studyDays: StudyDay[];
  practiceAttempts: PracticeAttempt[];
  dailyReports: DailyReport[];
  mistakes: Mistake[];
  caseSubmissions: CaseSubmission[];
  reviewEvents: ReviewEvent[];
  planRevisions: PlanRevision[];
}

export interface WeaknessCluster {
  id: string;
  subject: Subject;
  label: string;
  latestRate?: number;
  /** 最近两次可靠有序的正式成绩下降值，单位为百分点；无可靠顺序时不生成。 */
  rateDropPercentagePoints?: number;
  mistakeCount: number;
  masteryGrade?: "A" | "B" | "C" | "D";
  consecutiveBelowSixtyCount: number;
}

export interface NextDayAdjustmentInput {
  sourceDayId: string;
  nextDate: string;
  sleepHours?: number;
  fatigueLevel?: number;
  failedConcentrationBlockCount?: number;
  rateDropPercentagePoints?: number;
  weaknessClusters: WeaknessCluster[];
  mandatoryReviewMistakeIds: string[];
}

export interface NextDayAdjustment {
  nextDate: string;
  topClusters: WeaknessCluster[];
  firstSessionFocus?: string;
  overloadProtectionApplied: boolean;
  overloadReasons: string[];
  reduceNewKnowledgeMinutes: 0 | 120;
  maxMacroBlockChanges: 2;
  mandatoryContinuity: string[];
  mandatoryReviewMistakeIds: string[];
  clusterActions: Array<{
    clusterId: string;
    action: "rebuild_concept" | "d1_review" | "d3_review" | "d7_review" | "maintenance";
  }>;
}
